import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { compressImage, deleteStorageImage } from '../lib/imageUtils';
import { Group, GroupInvite, GroupMember, Tool, Category, LoanHistory, Notification, ToolComment } from '../types';
import { useInventory } from './inventory';

interface GroupState {
  groups: Group[];
  currentGroupId: string | null;
  members: GroupMember[];
  tools: Tool[];
  categories: Category[];
  notifications: Notification[];
  loanHistory: LoanHistory[];
  comments: ToolComment[];
  groupInvites: GroupInvite[];
  loading: boolean;
  unreadCount: number;

  fetchGroups: (userId: string) => Promise<void>;
  setCurrentGroup: (groupId: string) => Promise<void>;
  createGroup: (name: string, description: string, userId: string) => Promise<string | null>;
  joinByCode: (code: string, userId: string) => Promise<string | null>;
  updateGroup: (groupId: string, data: { name: string; description: string }) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  updateMemberRole: (memberId: string, role: 'admin' | 'member') => Promise<void>;

  fetchMembers: (groupId: string) => Promise<void>;
  fetchTools: (groupId: string) => Promise<void>;
  fetchCategories: (groupId: string) => Promise<void>;
  fetchNotifications: (userId: string) => Promise<void>;
  fetchLoanHistory: (userId: string) => Promise<void>;
  fetchComments: (toolId: string) => Promise<ToolComment[]>;
  fetchMyTools: (userId: string) => Promise<Tool[]>;
  fetchMyLoans: (userId: string) => Promise<any[]>;
  fetchMyLentOut: (userId: string) => Promise<any[]>;

  addTool: (data: { name: string; category: string; category_id: string | null; description: string; emoji: string; image_url: string | null; due_date: string | null }, userId: string, groupIds?: string[]) => Promise<void>;
  deleteTool: (toolId: string) => Promise<void>;
  requestLoan: (toolId: string, dueDate?: string | null) => Promise<void>;
  approveLoan: (toolId: string) => Promise<void>;
  rejectLoan: (toolId: string) => Promise<void>;
  cancelLoanRequest: (toolId: string) => Promise<void>;
  addToolToGroup: (toolId: string, groupId: string) => Promise<void>;
  shareToolsWithGroup: (toolIds: string[], groupId: string) => Promise<void>;
  removeToolFromGroup: (toolId: string, groupId: string) => Promise<void>;
  fetchToolGroups: (toolId: string) => Promise<string[]>;
  updateTool: (toolId: string, data: Partial<Tool>) => Promise<void>;
  loanOut: (toolId: string, toUserId: string, dueDate: string | null) => Promise<void>;
  returnTool: (toolId: string) => Promise<void>;
  markBorrowed: (toolId: string, userId: string) => Promise<void>;

  addCategory: (name: string, emoji: string, groupId: string, userId: string) => Promise<void>;
  updateCategory: (categoryId: string, name: string, emoji: string) => Promise<void>;
  updateToolsCategory: (fromCatName: string, toCatName: string, toCatId: string) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;

  addComment: (toolId: string, userId: string, comment: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;

  markNotificationsRead: () => Promise<void>;
  subscribeToNotifications: (userId: string, onNew: (n: Notification) => void) => () => void;

  currentGroup: () => Group | undefined;
  uploadImage: (file: File) => Promise<string | null>;
  sendInviteEmail: (toEmail: string, inviterName: string, groupName: string, inviteCode: string) => Promise<string | null>;
  deleteGroup: (groupId: string) => Promise<string | null>;
  leaveGroup: (groupId: string, userId: string) => Promise<string | null>;
  fetchGroupInvites: (groupId: string) => Promise<void>;
  deleteGroupInvite: (inviteId: string) => Promise<void>;
}

function makeCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map(b => chars[b % chars.length])
    .join('');
}

export const useGroups = create<GroupState>((set, get) => ({
  groups: [], currentGroupId: null, members: [], tools: [],
  categories: [], notifications: [], loanHistory: [], comments: [],
  groupInvites: [],
  loading: false, unreadCount: 0,

  fetchGroups: async (userId) => {
    set({ loading: true });
    const { data } = await supabase.from('group_members').select('group_id, groups(*)').eq('user_id', userId);
    const groups = (data || []).map((r: any) => r.groups).filter(Boolean);
    set({ groups });
    if (groups.length > 0 && !get().currentGroupId) {
      await get().setCurrentGroup(groups[0].id);
    } else {
      set({ loading: false });
    }
  },

  setCurrentGroup: async (groupId) => {
    set({ currentGroupId: groupId, loading: true });
    await Promise.all([get().fetchMembers(groupId), get().fetchTools(groupId), get().fetchCategories(groupId)]);
    set({ loading: false });
  },

  createGroup: async (name, description, userId) => {
    const { data, error } = await supabase.from('groups').insert({ name, description, invite_code: makeCode(), created_by: userId }).select().single();
    if (error) return error.message;
    await supabase.from('group_members').insert({ group_id: data.id, user_id: userId, role: 'admin' });
    // Insert default categories
    const defaultCats = [
      { name: 'Hage', emoji: '🌿', group_id: data.id, created_by: userId },
      { name: 'Bygg', emoji: '🏗️', group_id: data.id, created_by: userId },
      { name: 'Elektro', emoji: '🔌', group_id: data.id, created_by: userId },
      { name: 'Hus', emoji: '🏠', group_id: data.id, created_by: userId },
      { name: 'Verktøy', emoji: '🔧', group_id: data.id, created_by: userId },
      { name: 'Annet', emoji: '📦', group_id: data.id, created_by: userId },
    ];
    await supabase.from('categories').insert(defaultCats);
    set(s => ({ groups: [...s.groups, data] }));
    await get().setCurrentGroup(data.id);
    return null;
  },

  joinByCode: async (code, userId) => {
    const { data: group, error } = await supabase.from('groups').select('*').eq('invite_code', code.toUpperCase()).single();
    if (error || !group) return 'Fant ingen gruppe med den koden';
    const { data: existing } = await supabase.from('group_members').select('id').eq('group_id', group.id).eq('user_id', userId).single();
    if (existing) return 'Du er allerede med i denne gruppen';
    await supabase.from('group_members').insert({ group_id: group.id, user_id: userId, role: 'member' });
    // Notify group admin
    const admin = get().members.find(m => m.role === 'admin');
    if (admin) {
      const profile = await supabase.from('profiles').select('full_name').eq('id', userId).single();
      await supabase.from('notifications').insert({ user_id: admin.user_id, type: 'joined', from_user_id: userId, message: `${profile.data?.full_name} ble med i gruppen ${group.name}`, tool_id: null });
    }
    set(s => ({ groups: [...s.groups, group] }));
    await get().setCurrentGroup(group.id);
    return null;
  },

  updateGroup: async (groupId, data) => {
    await supabase.from('groups').update(data).eq('id', groupId);
    set(s => ({ groups: s.groups.map(g => g.id === groupId ? { ...g, ...data } : g) }));
  },

  removeMember: async (memberId) => {
    const member = get().members.find(m => m.id === memberId);
    if (member) {
      const theirToolIds = get().tools.filter(t => t.owner_id === member.user_id).map(t => t.id);
      if (theirToolIds.length > 0) {
        await supabase.from('tool_groups').delete().in('tool_id', theirToolIds).eq('group_id', member.group_id);
      }
      const { data: theirInvItems } = await supabase.from('inventory').select('id').eq('user_id', member.user_id);
      const theirInvIds = (theirInvItems || []).map((i: any) => i.id);
      if (theirInvIds.length > 0) {
        await supabase.from('inventory_groups').delete().in('inventory_id', theirInvIds).eq('group_id', member.group_id);
      }
    }
    await supabase.from('group_members').delete().eq('id', memberId);
    set(s => ({ members: s.members.filter(m => m.id !== memberId) }));
    if (member) useInventory.getState().fetchLoanableItems(member.group_id);
  },

  updateMemberRole: async (memberId, role) => {
    await supabase.from('group_members').update({ role }).eq('id', memberId);
    set(s => ({ members: s.members.map(m => m.id === memberId ? { ...m, role } : m) }));
  },

  fetchMembers: async (groupId) => {
    const { data } = await supabase.from('group_members').select('*, profile:profiles(*)').eq('group_id', groupId);
    set({ members: data || [] });
  },

  fetchTools: async (groupId) => {
  set({ loading: true });
  const { data } = await supabase
    .from('tool_groups')
    .select('tool_id, tools(*, owner:profiles!tools_owner_id_fkey(*), loaned_to:profiles!tools_loaned_to_id_fkey(*))')
    .eq('group_id', groupId);
  
  const tools = (data || [])
    .map((row: any) => row.tools)
    .filter(Boolean);
  
  set({ tools, loading: false });
},

  fetchCategories: async (groupId) => {
    const { data } = await supabase.from('categories').select('*').eq('group_id', groupId).order('name');
    set({ categories: data || [] });
  },

  fetchNotifications: async (userId) => {
    const { data } = await supabase.from('notifications').select('*, from_user:profiles!notifications_from_user_id_fkey(*), tool:tools(name,emoji)').eq('user_id', userId).order('created_at', { ascending: false }).limit(30);
    const unread = (data || []).filter(n => !n.read).length;
    set({ notifications: data || [], unreadCount: unread });
  },

  fetchLoanHistory: async (userId) => {
    const { data } = await supabase.from('loan_history').select('*, tool:tools(name,emoji), owner:profiles!loan_history_owner_id_fkey(full_name,avatar_color), borrower:profiles!loan_history_borrower_id_fkey(full_name,avatar_color)').or(`owner_id.eq.${userId},borrower_id.eq.${userId}`).order('loaned_at', { ascending: false }).limit(50);
    set({ loanHistory: data || [] });
  },

  fetchComments: async (toolId) => {
    const { data } = await supabase.from('tool_comments').select('*, profile:profiles(*)').eq('tool_id', toolId).order('created_at');
    set({ comments: data || [] });
    return data || [];
  },

  addTool: async (data, userId, groupIds = []) => {
    const { currentGroupId } = get();
    if (!currentGroupId) return;
    const { data: tool, error: toolError } = await supabase.from('tools')
      .insert({ ...data, group_id: currentGroupId, owner_id: userId, status: 'available', loaned_to_id: null })
      .select('*, owner:profiles!tools_owner_id_fkey(*), loaned_to:profiles!tools_loaned_to_id_fkey(*)')
      .single();
    if (toolError || !tool) throw new Error(toolError?.message || 'Kunne ikke opprette verktøy');
    const allGroups = [...new Set([currentGroupId, ...groupIds])];
    for (const gId of allGroups) {
      const { error: tgError } = await supabase.from('tool_groups').insert({ tool_id: tool.id, group_id: gId });
      if (tgError && gId === currentGroupId) throw new Error(`Feil ved gruppetilknytning: ${tgError.message}`);
    }
    await get().fetchTools(currentGroupId);
  },

  deleteTool: async (toolId) => {
    const { data: tool } = await supabase.from('tools').select('image_url').eq('id', toolId).single();
    await deleteStorageImage(tool?.image_url);
    await supabase.from('tools').delete().eq('id', toolId);
    set(s => ({ tools: s.tools.filter(t => t.id !== toolId) }));
  },

  requestLoan: async (toolId, dueDate) => {
    const { error } = await supabase.rpc('request_loan', { p_tool_id: toolId });
    if (error) throw new Error(error.message);
    if (dueDate) {
      await supabase.from('tools').update({ due_date: dueDate }).eq('id', toolId);
    }
    await get().fetchTools(get().currentGroupId!);
  },

  approveLoan: async (toolId) => {
    const { error } = await supabase.rpc('approve_loan', { p_tool_id: toolId });
    if (error) throw new Error(error.message);
    await get().fetchTools(get().currentGroupId!);
  },

  rejectLoan: async (toolId) => {
    const { error } = await supabase.rpc('reject_loan', { p_tool_id: toolId });
    if (error) throw new Error(error.message);
    await get().fetchTools(get().currentGroupId!);
  },

  cancelLoanRequest: async (toolId) => {
    const { error } = await supabase.rpc('cancel_loan_request', { p_tool_id: toolId });
    if (error) throw new Error(error.message);
    await get().fetchTools(get().currentGroupId!);
  },

  updateTool: async (toolId, data) => {
    if ('image_url' in data) {
      const { data: old } = await supabase.from('tools').select('image_url').eq('id', toolId).single();
      if (old?.image_url && old.image_url !== data.image_url) await deleteStorageImage(old.image_url);
    }
    await supabase.from('tools').update(data).eq('id', toolId);
    await get().fetchTools(get().currentGroupId!);
  },

  loanOut: async (toolId, toUserId, dueDate) => {
    const now = new Date().toISOString();
    await supabase.from('tools').update({ status: 'loaned', loaned_to_id: toUserId, loaned_at: now, due_date: dueDate }).eq('id', toolId);
    const tool = get().tools.find(t => t.id === toolId);
    // Save history
    await supabase.from('loan_history').insert({ tool_id: toolId, owner_id: tool?.owner_id, borrower_id: toUserId, loaned_at: now });
    // Notify borrower
    const message = `Du har fått låne ${tool?.emoji} ${tool?.name}`;
    await supabase.from('notifications').insert({ user_id: toUserId, type: 'loan_request', from_user_id: tool?.owner_id, tool_id: toolId, message });

    await get().fetchTools(get().currentGroupId!);
  },

  returnTool: async (toolId) => {
    const tool = get().tools.find(t => t.id === toolId);
    const { error } = await supabase.rpc('return_tool', { p_tool_id: toolId });
    if (error) throw new Error(error.message);
    if (tool?.owner_id) {
      const message = `${tool.emoji} ${tool.name} er levert tilbake`;
      await supabase.from('notifications').insert({ user_id: tool.owner_id, type: 'returned', from_user_id: tool.loaned_to_id, tool_id: toolId, message });
    }
    await get().fetchTools(get().currentGroupId!);
  },

  markBorrowed: async (toolId, userId) => {
    const now = new Date().toISOString();
    const tool = get().tools.find(t => t.id === toolId);
    await supabase.from('tools').update({ status: 'loaned', loaned_to_id: userId, loaned_at: now }).eq('id', toolId);
    await supabase.from('loan_history').insert({ tool_id: toolId, owner_id: tool?.owner_id, borrower_id: userId, loaned_at: now });
    if (tool?.owner_id) {
      // Get borrower name
      const { data: borrower } = await supabase.from('profiles').select('full_name').eq('id', userId).single();
      const message = `${borrower?.full_name ?? 'Noen'} vil låne ${tool.emoji} ${tool.name}`;
      await supabase.from('notifications').insert({ user_id: tool.owner_id, type: 'loan_request', from_user_id: userId, tool_id: toolId, message });

    }
    await get().fetchTools(get().currentGroupId!);
  },

  addCategory: async (name, emoji, groupId, userId) => {
    const { data } = await supabase.from('categories').insert({ name, emoji, group_id: groupId, created_by: userId }).select().single();
    if (data) set(s => ({ categories: [...s.categories, data].sort((a, b) => a.name.localeCompare(b.name)) }));
  },

  updateToolsCategory: async (fromCatName, toCatName, toCatId) => {
    const { currentGroupId } = get();
    if (!currentGroupId) return;
    await supabase.from('tools')
      .update({ category: toCatName, category_id: toCatId })
      .eq('category', fromCatName)
      .eq('group_id', currentGroupId);
    await get().fetchTools(currentGroupId);
  },

  updateCategory: async (categoryId, name, emoji) => {
    await supabase.from('categories').update({ name, emoji }).eq('id', categoryId);
    set(s => ({
      categories: s.categories.map(c => c.id === categoryId ? { ...c, name, emoji } : c)
    }));
  },

  deleteCategory: async (categoryId) => {
    await supabase.from('categories').delete().eq('id', categoryId);
    set(s => ({ categories: s.categories.filter(c => c.id !== categoryId) }));
  },

  addComment: async (toolId, userId, comment) => {
    await supabase.from('tool_comments').insert({ tool_id: toolId, user_id: userId, comment });
    await get().fetchComments(toolId);
  },

  deleteComment: async (commentId) => {
    await supabase.from('tool_comments').delete().eq('id', commentId);
    set(s => ({ comments: s.comments.filter(c => c.id !== commentId) }));
  },

  markNotificationsRead: async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    await supabase.from('notifications').update({ read: true }).eq('user_id', data.user.id).eq('read', false);
    set(s => ({ notifications: s.notifications.map(n => ({ ...n, read: true })), unreadCount: 0 }));
  },

  subscribeToNotifications: (userId, onNew) => {
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, async (payload) => {
        await get().fetchNotifications(userId);
        onNew(payload.new as Notification);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },

  uploadImage: async (file) => {
    if (!file.type.startsWith('image/')) throw new Error('Bare bildefiler er tillatt');
    if (file.size > 10 * 1024 * 1024) throw new Error('Bildet er for stort (maks 10 MB)');
    const compressed = await compressImage(file);
    const path = `${Date.now()}.jpg`;
    const { error } = await supabase.storage.from('tool-images').upload(path, compressed);
    if (error) return null;
    const { data } = supabase.storage.from('tool-images').getPublicUrl(path);
    return data.publicUrl;
  },

  deleteGroup: async (groupId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('https://hzplqizxzdwmfdopedjf.supabase.co/functions/v1/delete-group', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ group_id: groupId }),
      });
      const data = await res.json();
      if (!res.ok) return data.error || 'Sletting feilet';
      // Remove group from local state
      set(s => {
        const remaining = s.groups.filter(g => g.id !== groupId);
        return {
          groups: remaining,
          currentGroupId: remaining.length > 0 ? remaining[0].id : null,
          tools: remaining.length > 0 ? s.tools : [],
          members: remaining.length > 0 ? s.members : [],
          categories: remaining.length > 0 ? s.categories : [],
        };
      });
      // Load next group if available
      const remaining = get().groups;
      if (remaining.length > 0) await get().setCurrentGroup(remaining[0].id);
      return null;
    } catch {
      return 'Noe gikk galt';
    }
  },

  sendInviteEmail: async (toEmail, inviterName, groupName, inviteCode) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return 'Sesjonen er utløpt – logg ut og inn igjen';
      const res = await fetch(`https://hzplqizxzdwmfdopedjf.supabase.co/functions/v1/send-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ to: toEmail, inviterName, groupName, inviteCode }),
      });
      const data = await res.json();
      if (!res.ok) return data.error || 'Sending feilet';
      return null;
    } catch {
      return 'Noe gikk galt';
    }
  },

  addToolToGroup: async (toolId, groupId) => {
    await supabase.from('tool_groups').insert({ tool_id: toolId, group_id: groupId });
    await get().fetchTools(get().currentGroupId!);
  },

  shareToolsWithGroup: async (toolIds, groupId) => {
    if (toolIds.length === 0) return;
    const rows = toolIds.map(toolId => ({ tool_id: toolId, group_id: groupId }));
    const { error } = await supabase.from('tool_groups').insert(rows);
    if (error) throw new Error(error.message);
    await get().fetchTools(groupId);
  },

  fetchMyTools: async (userId: string) => {
    const { data } = await supabase
      .from('tools')
      .select('*, owner:profiles!tools_owner_id_fkey(*)')
      .eq('owner_id', userId);
    return data || [];
  },

  fetchMyLoans: async (userId: string) => {
    const { data } = await supabase
      .from('tools')
      .select('*, owner:profiles!tools_owner_id_fkey(*), loaned_to:profiles!tools_loaned_to_id_fkey(*), tool_groups(group_id, groups(id, name))')
      .eq('loaned_to_id', userId)
      .in('status', ['loaned', 'pending']);
    return data || [];
  },

  fetchMyLentOut: async (userId: string) => {
    const { data } = await supabase
      .from('tools')
      .select('*, loaned_to:profiles!tools_loaned_to_id_fkey(*), tool_groups(group_id, groups(id, name))')
      .eq('owner_id', userId)
      .eq('status', 'loaned');
    return data || [];
  },

  removeToolFromGroup: async (toolId, groupId) => {
    await supabase.from('tool_groups').delete().eq('tool_id', toolId).eq('group_id', groupId);
    set(s => ({ tools: s.tools.filter(t => t.id !== toolId) }));
  },

  fetchToolGroups: async (toolId) => {
    const { data } = await supabase.from('tool_groups').select('group_id').eq('tool_id', toolId);
    return (data || []).map((row: any) => row.group_id);
  },

  fetchGroupInvites: async (groupId) => {
    const { data } = await supabase
      .from('group_invites')
      .select('*')
      .eq('group_id', groupId)
      .order('invited_at', { ascending: false });
    set({ groupInvites: data || [] });
  },

  deleteGroupInvite: async (inviteId) => {
    await supabase.from('group_invites').delete().eq('id', inviteId);
    set(s => ({ groupInvites: s.groupInvites.filter(i => i.id !== inviteId) }));
  },

  leaveGroup: async (groupId, userId) => {
    const admins = get().members.filter(m => m.role === 'admin');
    const myMember = get().members.find(m => m.user_id === userId);
    if (!myMember) return 'Fant ikke ditt medlemskap';
    const totalMembers = get().members.length;
    if (admins.length === 1 && myMember.role === 'admin' && totalMembers > 1) {
      return 'Du er eneste admin. Gjør et annet medlem til admin først, eller bruk "Slett gruppe" for å slette gruppen.';
    }
    const myToolIds = get().tools.filter(t => t.owner_id === userId).map(t => t.id);
    if (myToolIds.length > 0) {
      await supabase.from('tool_groups').delete().in('tool_id', myToolIds).eq('group_id', groupId);
    }
    // Fjern brukerens inventory-items fra gruppen
    const { data: myInvItems } = await supabase.from('inventory').select('id').eq('user_id', userId);
    const myInvIds = (myInvItems || []).map((i: any) => i.id);
    if (myInvIds.length > 0) {
      await supabase.from('inventory_groups').delete().in('inventory_id', myInvIds).eq('group_id', groupId);
    }
    await supabase.from('group_members').delete().eq('id', myMember.id);
    useInventory.setState({ loanableItems: [] });
    set(s => {
      const remaining = s.groups.filter(g => g.id !== groupId);
      return { groups: remaining, currentGroupId: remaining[0]?.id ?? null, tools: [], members: [], categories: [] };
    });
    const remaining = get().groups;
    if (remaining.length > 0) await get().setCurrentGroup(remaining[0].id);
    return null;
  },

  currentGroup: () => get().groups.find(g => g.id === get().currentGroupId),
}));